<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Sales Target vs Actual
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="pa-2">
                    <v-col lg="2" md="2" cols="12">
                      <v-select
                        label="Period Type"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="filters.period_type"
                        :items="[{ title: 'Monthly', value: 'monthly' }, { title: 'Quarterly', value: 'quarterly' }, { title: 'Annual', value: 'annual' }]"
                        autocomplete="off"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="From Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi mdi-calendar-month"
                        single-line
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="filters.from_date"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col cols="12" md="3">
                       <v-date-input
                        input-format="dd/mm/yyyy"
                        label="To Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi mdi-calendar-month"
                        single-line
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="filters.to_date"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="6">
                      <v-btn class="bg-brand" type="submit" ref="searchButton"
                        >Search</v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="reportData"
        :loading="loading"
        :items-per-page="-1"
        :search="search"
        :hover="true"
        class="elevation-1"
        density="comfortable"
        hide-default-footer
      >
      <template v-slot:item.index="{ index }">
        {{ ++index }}
      </template>

      <template #tfoot>
        <tr class="font-weight-bold">
          <td colspan="2" class="text-center">Total</td>
          <td class="text-left">{{ reportData.reduce((prev, curr) => { return +prev + +curr.target_amount }, 0) }}</td>
          <td class="text-left">{{  reportData.reduce((prev, curr) => { return +prev + +curr.actual_amount }, 0) }}</td>
          <td class="text-left">{{  reportData.reduce((prev, curr) => { return +prev + +curr.paid_amount }, 0) }}</td>
          <td class="text-left">{{  reportData.reduce((prev, curr) => { return +prev + +curr.due_amount }, 0) }}</td>
        </tr>
      </template>
      </v-data-table>

      <v-row class="pt-2">
        <v-col cols="6">
          <v-card class="pa-4" outlined>
            <v-card-title class="text-h6 font-weight-bold text-center">
              Sales Target vs Actual ({{ filters.period_type }})
            </v-card-title>
            <apexchart
              width="100%"
              height="400"
              type="bar"
              :options="chartOptions"
              :series="chartSeries"
            />
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card outlined>
            <v-card-title>Team Target Distribution (Split)</v-card-title>
            <v-data-table
              :headers="teamHeaders"
              :items="teamDistribution"
              class="elevation-1"
              :items-per-page="-1"
              hide-default-footer
            >
              <template #item.index="{ index }">{{ ++index }}</template>
              <template #item.split_target_amount="{ item }">
                {{ formatCurrency(item.split_target_amount) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- <v-row>
       
      </v-row> -->
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import ApexCharts from "vue3-apexcharts";
import Axios from "axios";
import formattedDate from "@/plugins/dateFormatter";
import { getTargetVsActual, getSalesTargetVsActual } from "@/store/api.js";

const panel = ref([0]);
const loading = ref(false);
const filters = ref({
  period_type: "monthly",
  from_date: "",
  to_date: "",
});
const chartOptions = ref({
  chart: { type: 'bar', height: 350 },
  xaxis: { categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], },
  yaxis: {
    labels: {
      formatter: (val) => Number(val).toLocaleString("en-BD"),
    },
  },
  dataLabels: { enabled: true },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "40%",
      borderRadius: 6,
    },
  },
  legend: {
    position: "top",
  },
  colors: ["#0288d1", "#43a047"],
  
});
const chartSeries = ref([]);
const teamDistribution = ref([]);

const teamHeaders = [
  { title: "#", key: "index", align: "center" },
  { title: "Team ID", key: "team_master_id" },
  { title: "Employee ID", key: "employee_id" },
  { title: "Split Target", key: "split_target_amount", align: "right" },
  { title: "Period", key: "period_type" },
  { title: "Start Date", key: "start_date" },
  { title: "End Date", key: "end_date" },
];

const reportData = ref([]);
const summary = ref({ total_target: 0, total_actual: 0, average_achievement: 0 });

const headers = [
  { title: "#", key: "index", align: "center", width: "5%" },
  { title: "Employee", key: "employee_name", align: "left" },
  { title: "Target Amount", key: "target_amount", align: "left" },
  { title: "Actual Amount", key: "actual_amount", align: "left" },
  { title: "Paid Amount", key: "paid_amount", align: "left" },
  { title: "Due Amount", key: "due_amount", align: "left" },
  { title: "Achievement", key: "achievement_percent", align: "center" },
];

const submit = async () => {
  try {
    loading.value = true;
    const { data } = await Axios.post(getSalesTargetVsActual, {
      period_type: filters.value.period_type,
      from_date: formattedDate(filters.value.from_date),
      to_date: formattedDate(filters.value.to_date),
    });

    chartOptions.value.xaxis.categories = data.chart.labels;
    chartSeries.value = data.chart.series;
    teamDistribution.value = data.team_distribution;

    let data1  = await Axios.post(getTargetVsActual, {
      period_type: filters.value.period_type,
      from_date: formattedDate(filters.value.from_date),
      to_date: formattedDate(filters.value.to_date),
    });

    data1 = data1.data;

    reportData.value = data1.data.map((row) => ({
      ...row,
      achievement:
        row.target_amount > 0
          ? (row.actual_amount / row.target_amount) * 100
          : 0,
    }));

    summary.value = {
      total_target: reportData.value.reduce((a, b) => a + b.target_amount, 0),
      total_actual: reportData.value.reduce((a, b) => a + b.actual_amount, 0),
      average_achievement:
        reportData.value.length > 0
          ? reportData.value.reduce(
              (a, b) => a + b.achievement,
              0
            ) / reportData.value.length
          : 0,
      };

  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const formatCurrency = (val) =>
  Number(val || 0).toLocaleString("en-BD", { minimumFractionDigits: 2 });
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
